import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, X, CheckCircle, AlertCircle, Loader2, Lock, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import CPFModal from "./CPFModal";
import { comPulseira } from "@/features/cliente-sessao";

const API = '/api';

// Mapeamento de erros do Mercado Pago para mensagens amigáveis
const getRejectMessage = (statusDetail) => {
  const messages = {
    'cc_rejected_insufficient_amount': 'Saldo insuficiente. Tente com outro cartão.',
    'cc_rejected_bad_filled_card_number': 'Número do cartão incorreto.',
    'cc_rejected_bad_filled_date': 'Data de validade incorreta.',
    'cc_rejected_bad_filled_security_code': 'Código de segurança incorreto.',
    'cc_rejected_bad_filled_other': 'Dados do cartão incorretos. Verifique as informações.',
    'cc_rejected_high_risk': 'Pagamento recusado por segurança. Tente com outro cartão.',
    'cc_rejected_call_for_authorize': 'Ligue para a operadora do cartão para autorizar.',
    'cc_rejected_card_disabled': 'Cartão desabilitado. Contate a operadora.',
    'cc_rejected_max_attempts': 'Limite de tentativas excedido. Tente com outro cartão.',
    'cc_rejected_duplicated_payment': 'Pagamento duplicado. Aguarde ou tente mais tarde.',
    'cc_rejected_card_type_not_allowed': 'Tipo de cartão não permitido.',
    'cc_rejected_other_reason': 'Pagamento recusado. Tente com outro cartão.',
  };
  return messages[statusDetail] || 'Pagamento recusado. Tente novamente com outro cartão.';
};

const getCardBrandName = (paymentMethodId) => {
  const brands = {
    'visa': 'Visa',
    'master': 'Mastercard',
    'amex': 'Amex',
    'elo': 'Elo',
    'hipercard': 'Hipercard',
    'diners': 'Diners',
    'discover': 'Discover',
    'debvisa': 'Visa Débito',
    'debmaster': 'Mastercard Débito',
  };
  return brands[paymentMethodId] || paymentMethodId || 'Cartão';
};

export default function CartaoOnlineModal({
  open,
  onClose,
  orderId,
  customerId,
  amount,
  onPaymentConfirmed
}) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState('loading'); // loading, form, processing, approved, rejected
  const [errorMessage, setErrorMessage] = useState('');
  const [showCPFModal, setShowCPFModal] = useState(false);
  const [cardFormReady, setCardFormReady] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Saved cards state
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedCard, setSelectedSavedCard] = useState(null);
  const [savedCardCvv, setSavedCardCvv] = useState('');
  const [useNewCard, setUseNewCard] = useState(false);
  const [deletingCardId, setDeletingCardId] = useState(null);

  const mpInstanceRef = useRef(null);
  const cardFormRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const pendingResolveRef = useRef(null);
  const lastTokenRef = useRef(null);

  const isNewCardMode = useNewCard || savedCards.length === 0;

  const cleanupCardForm = useCallback(() => {
    if (cardFormRef.current) {
      try {
        cardFormRef.current.unmount();
      } catch (e) {
        // Ignore cleanup errors
      }
      cardFormRef.current = null;
    }
    mpInstanceRef.current = null;
    hasInitializedRef.current = false;
    setCardFormReady(false);
  }, []);

  // Fetch saved cards
  useEffect(() => {
    if (open && customerId) {
      axios.get(`${API}/payments/card-online/saved-cards/${customerId}`, comPulseira())
        .then(res => {
          const cards = res.data || [];
          setSavedCards(cards);
          if (cards.length > 0) {
            setSelectedSavedCard(cards[0]);
            setUseNewCard(false);
          } else {
            setUseNewCard(true);
          }
        })
        .catch(() => {
          setSavedCards([]);
          setUseNewCard(true);
        });
    }
  }, [open, customerId]);

  const initializeCardForm = useCallback(async () => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    setLoading(true);
    setStatus('loading');

    try {
      // Buscar public key do backend
      const response = await axios.get(`${API}/public/mp-public-key`);
      const publicKey = response.data.public_key;

      if (!publicKey) {
        toast.error("Cartão Online não configurado");
        setStatus('form');
        setLoading(false);
        return;
      }

      // Verificar se MercadoPago SDK está carregado
      if (!window.MercadoPago) {
        toast.error("SDK de pagamento não carregado. Recarregue a página.");
        setStatus('form');
        setLoading(false);
        return;
      }

      // Inicializar MercadoPago SDK
      const mp = new window.MercadoPago(publicKey, { locale: 'pt-BR' });
      mpInstanceRef.current = mp;

      // Pequeno delay para garantir que os containers HTML estejam renderizados
      await new Promise(resolve => setTimeout(resolve, 100));

      // Criar CardForm
      const cardForm = mp.cardForm({
        amount: String(amount),
        iframe: true,
        form: {
          id: "card-payment-form",
          cardNumber: {
            id: "card-number-container",
            placeholder: "0000 0000 0000 0000",
          },
          expirationDate: {
            id: "expiration-date-container",
            placeholder: "MM/AA",
          },
          securityCode: {
            id: "security-code-container",
            placeholder: "123",
          },
          cardholderName: {
            id: "cardholder-name",
            placeholder: "Nome como está no cartão",
          },
          issuer: {
            id: "issuer-select",
          },
          installments: {
            id: "installments-select",
          },
          identificationType: {
            id: "identification-type-select",
          },
          identificationNumber: {
            id: "identification-number",
            placeholder: "000.000.000-00",
          },
        },
        callbacks: {
          onFormMounted: (error) => {
            if (error) {
              console.error("CardForm mount error:", error);
              toast.error("Erro ao carregar formulário de cartão");
            } else {
              setCardFormReady(true);
            }
            setLoading(false);
            setStatus('form');
          },
          onSubmit: (event) => {
            event.preventDefault();
            // SDK criou o token — resolver a promise pendente com os dados do form
            if (cardFormRef.current && pendingResolveRef.current) {
              const data = cardFormRef.current.getCardFormData();
              pendingResolveRef.current(data);
              pendingResolveRef.current = null;
            }
          },
          onFetching: () => {
            return () => {};
          },
        },
      });

      cardFormRef.current = cardForm;
    } catch (error) {
      console.error("Erro ao inicializar CardForm:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || "Cartão Online não disponível");
      } else {
        toast.error("Erro ao inicializar pagamento com cartão");
      }
      setStatus('form');
      setLoading(false);
    }
  }, [amount]);

  useEffect(() => {
    if (open && orderId) {
      initializeCardForm();
    }
    return () => {
      cleanupCardForm();
    };
  }, [open, orderId, initializeCardForm, cleanupCardForm]);

  // Detectar foco nos iframes do SDK MercadoPago
  // Eventos focus/blur no iframe cross-origin não são confiáveis em todos os browsers.
  // Solução: polling de document.activeElement — quando um iframe cross-origin tem foco,
  // document.activeElement retorna o elemento <iframe> no documento pai.
  useEffect(() => {
    if (!open || !cardFormReady) return;

    const iframeFieldMap = {
      'card-number-container': 'cardNumber',
      'expiration-date-container': 'expirationDate',
      'security-code-container': 'securityCode',
    };
    const iframeFieldIds = new Set(Object.values(iframeFieldMap));

    const checkFocus = () => {
      const activeEl = document.activeElement;

      if (activeEl && activeEl.tagName === 'IFRAME') {
        for (const [containerId, fieldId] of Object.entries(iframeFieldMap)) {
          const container = document.getElementById(containerId);
          if (container && container.contains(activeEl)) {
            setFocusedField(fieldId);
            return;
          }
        }
      }

      // Só limpar se o campo focado atual é um dos campos iframe
      // (não interferir com onFocus/onBlur dos inputs regulares)
      setFocusedField(prev => iframeFieldIds.has(prev) ? null : prev);
    };

    const interval = setInterval(checkFocus, 150);

    return () => clearInterval(interval);
  }, [open, cardFormReady]);

  // Auto-save card after successful payment
  const trySaveCard = useCallback(async (token) => {
    if (!customerId || !token) return;
    try {
      await axios.post(`${API}/payments/card-online/save-card`, {
        customer_id: customerId,
        token: token,
      }, comPulseira());
    } catch (e) {
      // Silently fail — card just won't be saved for future use
      console.log("Cartão não pôde ser salvo para uso futuro:", e);
    }
  }, [customerId]);

  const handlePaymentResult = useCallback(async (result, token) => {
    if (result.status === 'approved') {
      setStatus('approved');
      toast.success("Pagamento aprovado!");
      if (onPaymentConfirmed) onPaymentConfirmed();
      // Try to save card for future use (only for new cards)
      if (isNewCardMode && token) {
        trySaveCard(token);
      }
      setTimeout(() => onClose(), 2500);
    } else if (result.status === 'rejected') {
      setStatus('rejected');
      setErrorMessage(getRejectMessage(result.status_detail));
    } else if (result.status === 'in_process') {
      setStatus('approved');
      toast.success("Pagamento em processamento. Você será notificado quando for confirmado.");
      if (onPaymentConfirmed) onPaymentConfirmed();
      if (isNewCardMode && token) {
        trySaveCard(token);
      }
      setTimeout(() => onClose(), 2500);
    } else {
      setErrorMessage("Status de pagamento desconhecido. Entre em contato com o suporte.");
      setStatus('form');
    }
  }, [isNewCardMode, onClose, onPaymentConfirmed, trySaveCard]);

  // Submit with NEW card (existing flow)
  const handleNewCardSubmit = async () => {
    if (!cardFormRef.current || processing) return;

    setProcessing(true);
    setStatus('processing');
    setErrorMessage('');

    try {
      // Triggar submit do form para o SDK do MercadoPago criar o token
      const cardFormData = await new Promise((resolve, reject) => {
        pendingResolveRef.current = resolve;

        const form = document.getElementById('card-payment-form');
        if (form) {
          form.requestSubmit();
        } else {
          pendingResolveRef.current = null;
          reject(new Error('Formulário não encontrado'));
        }

        // Timeout de segurança (15s)
        setTimeout(() => {
          if (pendingResolveRef.current) {
            pendingResolveRef.current = null;
            reject(new Error('Tempo esgotado ao criar token do cartão'));
          }
        }, 15000);
      });

      const {
        token,
        paymentMethodId,
        issuerId,
        identificationNumber,
      } = cardFormData;

      if (!token) {
        setErrorMessage("Erro ao processar dados do cartão. Verifique as informações e tente novamente.");
        setStatus('form');
        setProcessing(false);
        return;
      }

      lastTokenRef.current = token;

      const response = await axios.post(
        `${API}/payments/card-online/process`,
        {
          nucleo_order_id: orderId,
          token: token,
          payment_method_id: paymentMethodId,
          installments: 1,
          issuer_id: issuerId || null,
          payer_cpf: identificationNumber || null,
        },
        { timeout: 30000 }
      );

      await handlePaymentResult(response.data, token);
    } catch (error) {
      console.error("Erro ao processar pagamento:", error);
      if (
        error.response?.status === 409 &&
        error.response?.headers?.['x-error-code'] === 'CPF_REQUIRED'
      ) {
        setShowCPFModal(true);
        setStatus('form');
      } else {
        setErrorMessage(
          error.response?.data?.detail || "Erro ao processar pagamento. Tente novamente."
        );
        setStatus('form');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Submit with SAVED card
  const handleSavedCardSubmit = async () => {
    if (!selectedSavedCard || !savedCardCvv || processing) return;

    if (!mpInstanceRef.current) {
      toast.error("SDK de pagamento não inicializado. Aguarde.");
      return;
    }

    setProcessing(true);
    setStatus('processing');
    setErrorMessage('');

    try {
      // Create token from saved card + CVV
      const tokenData = await mpInstanceRef.current.createCardToken({
        cardId: selectedSavedCard.id,
        securityCode: savedCardCvv,
      });

      if (!tokenData || !tokenData.id) {
        setErrorMessage("Erro ao processar cartão salvo. Tente novamente.");
        setStatus('form');
        setProcessing(false);
        return;
      }

      const response = await axios.post(
        `${API}/payments/card-online/process`,
        {
          nucleo_order_id: orderId,
          token: tokenData.id,
          payment_method_id: selectedSavedCard.payment_method?.id || "visa",
          installments: 1,
          issuer_id: selectedSavedCard.issuer?.id ? String(selectedSavedCard.issuer.id) : null,
          payer_cpf: null,
        },
        { timeout: 30000 }
      );

      await handlePaymentResult(response.data, null); // null token = don't save again
    } catch (error) {
      console.error("Erro ao processar pagamento com cartão salvo:", error);
      if (
        error.response?.status === 409 &&
        error.response?.headers?.['x-error-code'] === 'CPF_REQUIRED'
      ) {
        setShowCPFModal(true);
        setStatus('form');
      } else {
        setErrorMessage(
          error.response?.data?.detail || "Erro ao processar pagamento. Tente novamente."
        );
        setStatus('form');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (isNewCardMode) {
      handleNewCardSubmit();
    } else {
      handleSavedCardSubmit();
    }
  };

  const handleRetry = () => {
    setStatus('form');
    setErrorMessage('');
    setSavedCardCvv('');
    axios.patch(`${API}/pedidos/${orderId}`, {
      payment_status: 'pending'
    }).catch(() => {});
  };

  const handleCPFSaved = () => {
    setShowCPFModal(false);
    handleSubmit();
  };

  const handleDeleteCard = async (cardId) => {
    setDeletingCardId(cardId);
    try {
      await axios.delete(`${API}/payments/card-online/saved-cards/${customerId}/${cardId}`, comPulseira());
      const remaining = savedCards.filter(c => c.id !== cardId);
      setSavedCards(remaining);
      if (selectedSavedCard?.id === cardId) {
        if (remaining.length > 0) {
          setSelectedSavedCard(remaining[0]);
        } else {
          setSelectedSavedCard(null);
          setUseNewCard(true);
        }
      }
      toast.success("Cartão removido");
    } catch (e) {
      toast.error("Erro ao remover cartão");
    } finally {
      setDeletingCardId(null);
    }
  };

  const canSubmit = isNewCardMode
    ? cardFormReady && !processing
    : selectedSavedCard && savedCardCvv.length >= 3 && !processing;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideClose className="w-[95vw] sm:max-w-[440px] p-0 gap-0 max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border-0">
          {/* Barra de accent superior */}
          <div className="h-1 w-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 rounded-t-2xl" />

          <DialogHeader className="px-6 pt-5 pb-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-bold flex items-center gap-3 text-gray-900 dark:text-gray-100">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-md shadow-orange-500/25">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="block">Pagamento com Cartão</span>
                  <span className="block text-xs font-normal text-gray-400 dark:text-gray-500 mt-0.5">Crédito ou Débito</span>
                </div>
              </DialogTitle>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </DialogHeader>

          {/* Valor do pedido — sempre visível no topo */}
          {amount && amount > 0 && (
            <div className="mx-6 mt-4 mb-1 flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">Total a pagar</span>
              <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
                R$ {Number(amount).toFixed(2).replace('.', ',')}
              </span>
            </div>
          )}

          <div className="px-6 pt-4 pb-6">
            {/* Loading */}
            {status === 'loading' && (
              <div className="text-center py-14">
                <div className="w-14 h-14 rounded-full border-[3px] border-gray-200 dark:border-gray-700 border-t-orange-500 animate-spin mx-auto mb-5" />
                <p className="text-gray-600 dark:text-gray-400 font-medium">Preparando pagamento seguro...</p>
                <p className="text-gray-400 dark:text-gray-500 text-xs mt-1.5">Carregando formulário de pagamento</p>
              </div>
            )}

            {/* Approved */}
            {status === 'approved' && (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-green-50 dark:bg-green-500/10 flex items-center justify-center mx-auto mb-5 ring-4 ring-green-100 dark:ring-green-500/20">
                  <CheckCircle className="w-11 h-11 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-1.5">Pagamento Aprovado!</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Seu pedido foi confirmado com sucesso.</p>
              </div>
            )}

            {/* Processing */}
            {status === 'processing' && (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center mx-auto mb-5 ring-4 ring-orange-100 dark:ring-orange-500/20">
                  <Loader2 className="w-9 h-9 text-orange-500 animate-spin" />
                </div>
                <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg">Processando pagamento...</p>
                <p className="text-gray-400 dark:text-gray-500 text-xs mt-2">Aguarde, não feche esta janela</p>
              </div>
            )}

            {/* Form area — visible when status is form or rejected */}
            <div className={status === 'form' || status === 'rejected' ? '' : 'hidden'}>
              {/* Error banner */}
              {(status === 'rejected' || errorMessage) && (
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4 mb-5 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-red-700 dark:text-red-400 font-semibold text-sm">Pagamento não aprovado</p>
                    <p className="text-red-600 dark:text-red-300 text-sm mt-1">{errorMessage}</p>
                    {status === 'rejected' && (
                      <button
                        onClick={handleRetry}
                        className="text-orange-600 dark:text-orange-400 underline text-sm mt-2 hover:text-orange-700 dark:hover:text-orange-300 font-medium"
                      >
                        Tentar novamente
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Saved Cards Section */}
              {savedCards.length > 0 && (
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2.5 uppercase tracking-wide">
                    Cartões Salvos
                  </label>
                  <div className="space-y-2">
                    {savedCards.map((card) => (
                      <div
                        key={card.id}
                        onClick={() => {
                          setSelectedSavedCard(card);
                          setUseNewCard(false);
                          setSavedCardCvv('');
                        }}
                        className={`relative flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                          !useNewCard && selectedSavedCard?.id === card.id
                            ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10 ring-1 ring-orange-500/30'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {/* Radio indicator */}
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          !useNewCard && selectedSavedCard?.id === card.id
                            ? 'border-orange-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {!useNewCard && selectedSavedCard?.id === card.id && (
                            <div className="w-2 h-2 rounded-full bg-orange-500" />
                          )}
                        </div>

                        {/* Card icon */}
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center flex-shrink-0">
                          <CreditCard className="w-4.5 h-4.5 text-gray-500 dark:text-gray-400" />
                        </div>

                        {/* Card info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                              {getCardBrandName(card.payment_method?.id)}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              ****{card.last_four_digits}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                            {card.cardholder?.name || ''}{card.expiration_month && card.expiration_year ? ` · ${String(card.expiration_month).padStart(2, '0')}/${card.expiration_year}` : ''}
                          </p>
                        </div>

                        {/* Delete button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCard(card.id);
                          }}
                          disabled={deletingCardId === card.id}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                          aria-label="Remover cartão"
                        >
                          {deletingCardId === card.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ))}

                    {/* New card option */}
                    <div
                      onClick={() => {
                        setUseNewCard(true);
                        setSelectedSavedCard(null);
                        setSavedCardCvv('');
                      }}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        useNewCard
                          ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10 ring-1 ring-orange-500/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        useNewCard
                          ? 'border-orange-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {useNewCard && (
                          <div className="w-2 h-2 rounded-full bg-orange-500" />
                        )}
                      </div>
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-500/20 dark:to-orange-600/20 flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-4.5 h-4.5 text-orange-500" />
                      </div>
                      <span className="font-medium text-sm text-gray-700 dark:text-gray-300">
                        Usar outro cartão
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* CVV field for saved card mode */}
              {!isNewCardMode && selectedSavedCard && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                    Código de Segurança (CVV)
                  </label>
                  <input
                    type="password"
                    // CVV do cartão salvo, não senha: sem isso o navegador lê a
                    // tela como formulário de login e escreve o usuário salvo
                    // em outro campo de texto do cardápio.
                    autoComplete="cc-csc"
                    inputMode="numeric"
                    maxLength={4}
                    value={savedCardCvv}
                    onChange={(e) => setSavedCardCvv(e.target.value.replace(/\D/g, ''))}
                    placeholder="CVV"
                    autoFocus
                    className="h-[50px] w-32 border border-gray-200 dark:border-gray-600 rounded-xl px-4 text-center text-lg font-mono tracking-[0.3em] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                    Código de 3 ou 4 dígitos no verso do cartão
                  </p>
                </div>
              )}

              {/* New card form — always in DOM for SDK, hidden when using saved card */}
              <form id="card-payment-form" onSubmit={(e) => e.preventDefault()}>
                <div className={isNewCardMode ? 'space-y-4' : 'hidden'}>
                  {/* Número do Cartão */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                      Número do Cartão
                    </label>
                    <div
                      id="card-number-container"
                      className={`h-[50px] border rounded-xl overflow-hidden bg-white dark:bg-gray-800 transition-all cursor-text ${
                        focusedField === 'cardNumber'
                          ? 'border-orange-500 ring-2 ring-orange-500/20'
                          : 'border-gray-200 dark:border-gray-600'
                      }`}
                    />
                  </div>

                  {/* Validade + CVV */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                        Validade
                      </label>
                      <div
                        id="expiration-date-container"
                        className={`h-[50px] border rounded-xl overflow-hidden bg-white dark:bg-gray-800 transition-all cursor-text ${
                          focusedField === 'expirationDate'
                            ? 'border-orange-500 ring-2 ring-orange-500/20'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                        CVV
                      </label>
                      <div
                        id="security-code-container"
                        className={`h-[50px] border rounded-xl overflow-hidden bg-white dark:bg-gray-800 transition-all cursor-text ${
                          focusedField === 'securityCode'
                            ? 'border-orange-500 ring-2 ring-orange-500/20'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Nome no Cartão */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                      Nome no Cartão
                    </label>
                    <input
                      id="cardholder-name"
                      type="text"
                      onFocus={() => setFocusedField('cardholderName')}
                      onBlur={() => setFocusedField(null)}
                      className="h-[50px] w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 text-base text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    />
                  </div>

                  {/* CPF */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                      CPF do Titular
                    </label>
                    <div className="flex gap-2">
                      <select
                        id="identification-type-select"
                        onFocus={() => setFocusedField('identificationType')}
                        onBlur={() => setFocusedField(null)}
                        className="h-[50px] w-20 border border-gray-200 dark:border-gray-600 rounded-xl px-2 text-base text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                      />
                      <input
                        id="identification-number"
                        type="text"
                        onFocus={() => setFocusedField('identificationNumber')}
                        onBlur={() => setFocusedField(null)}
                        className="flex-1 h-[50px] border border-gray-200 dark:border-gray-600 rounded-xl px-4 text-base text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Hidden SDK fields — always in DOM */}
                <select id="issuer-select" className="hidden" />
                <select id="installments-select" className="hidden" />
                <button type="submit" className="hidden" />
              </form>

              {/* Payment button and footer */}
              <div className="pt-4">
                <Button
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  className="w-full h-[52px] bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-[15px] rounded-xl shadow-lg shadow-orange-500/25 disabled:opacity-50 disabled:shadow-none disabled:from-gray-300 disabled:to-gray-400 transition-all"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Pagar R$ {Number(amount).toFixed(2).replace('.', ',')}
                    </>
                  )}
                </Button>

                {/* Trust footer */}
                <div className="flex items-center justify-center gap-2 mt-4 pb-1">
                  <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                    <ShieldCheck className="w-4 h-4" />
                    <span className="text-[11px] font-medium">Pagamento seguro</span>
                  </div>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                    <Lock className="w-3.5 h-3.5" />
                    <span className="text-[11px] font-medium">Dados criptografados</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CPFModal
        open={showCPFModal}
        onClose={() => setShowCPFModal(false)}
        customerId={customerId}
        onSaved={handleCPFSaved}
      />
    </>
  );
}
